import { capitalize, concat, find, isEmpty, values } from "lodash";
import React, { useMemo } from "react";
import { Multiselect } from "react-widgets";
import { useLabels } from "../../context/labels";
import classNames from "../../utils/classNames";
import { Entity } from "../../utils/constants";
import { Label } from "../../utils/types";
import useFilteredLabels, {
  LabelFilterKey
} from "../../utils/useFilteredLabels";

interface Props {
  className?: string;
  entity: Entity;
  selectedLabels: Label[];
  withWalletLabels: boolean;
  smartLabels: Label[];
  filterKey: LabelFilterKey;
  onClose: () => void;
}

const SelectFilteredLabels: React.FC<Props> = ({
  className,
  entity,
  selectedLabels,
  withWalletLabels,
  smartLabels,
  filterKey,
  onClose
}) => {
  const { labels } = useLabels();
  const { toggleFilteredLabel } = useFilteredLabels();

  const labelOptions = useMemo(() => {
    const persistedLabels = values(labels).filter((label) => {
      if (selectedLabels.includes(label)) return false;

      if (withWalletLabels && label.type === Entity.wallet) {
        return true;
      }

      return label.type === entity;
    });

    return concat(
      persistedLabels,
      smartLabels.filter((label) => !selectedLabels.includes(label))
    );
  }, [labels, smartLabels, selectedLabels, withWalletLabels, entity]);

  const extraProps = useMemo(
    () =>
      !withWalletLabels && isEmpty(smartLabels)
        ? {}
        : {
            groupBy: (label: Label) =>
              !!find(smartLabels, { uuid: label.uuid })
                ? "Filters"
                : `${capitalize(label.type)} labels`
          },
    [smartLabels, withWalletLabels]
  );

  return (
    <Multiselect
      containerClassName={classNames(
        "dark:bg-gray-700 dark:text-white",
        className
      )}
      placeholder="Select filters"
      data={labelOptions}
      value={selectedLabels}
      allowCreate={false}
      onChange={(newSelectedLabels, metadata) => {
        toggleFilteredLabel(filterKey, metadata.dataItem.uuid);

        if (metadata.action === "insert") {
          onClose();
        }
      }}
      textField="name"
      filter="contains"
      messages={{
        emptyList: "No labels available"
      }}
      {...extraProps}
    />
  );
};

export default SelectFilteredLabels;
